<template>
  <nav class="nav al-global-navigation">
    <div class="al-global-primary-navigation">
      <!--
        @slot Container to display the logo or one main action
      -->
      <slot name="logo" />
      <!-- Primary Navigations Routes -->
      <a
        v-for="(navModule, index) in navigation"
        :key="navModule.id || index"
        :href="navModule.link"
        class="al-nav-link al-pages-link"
        :class="{ 'al-nav-link-active': navModule.current }"
        test-id="vertical-nav_item"
        @click.prevent="clickModule(navModule.id)"
      >
        <i class="icon" :class="navModule.icon" aria-hidden="true" />
        <span class="al-tooltiptext">{{ navModule.name }}</span>
      </a>
    </div>
    <div class="al-global-secondary-navigation">
      <!--
        @slot Container to add extra functions or actions
      -->
      <slot name="secondary" />
    </div>
  </nav>
</template>

<script>
/**
 * Menu navigation displayed vertically, use for main navigation;
 * it just show links with icon and the name as a tooltip, but it
 * also have a slot to show a logo in the top of the nav bar and
 * another slot to put inside extra components like buttons or
 * action icons.
 * @displayName al-vertical-navigation
 */
export default {
  name: 'AlVerticalNavigation',
  props: {
    /**
     * Array of javascript objects with menu items to show.
     * @values object keys: id, link, current(boolean), icon, name
     */
    navigation: {
      type: Array,
      required: true
    }
  },
  methods: {
    clickModule(moduleId) {
      /**
       * Event to emit when the user click over a link and return
       * which object was clicked
       * @property {string} playload String with the page id.
       */
      this.$emit('change-page', moduleId);
    }
  }
};
</script>

<style lang="scss" scoped>
.al-global-navigation {
  align-items: center;

  background-color: color(white, darken, 1);
  box-shadow: inset -8px 0 8px 0 rgba(54, 64, 63, 0.07);
  display: flex;
  flex-flow: column;
  height: 100%;
  justify-content: space-between;

  min-width: spacing(13);
  padding: spacing(4) spacing(3);

  .al-global-primary-navigation,
  .al-global-secondary-navigation {
    align-items: center;
    display: flex;
    flex-flow: column;

    .icon {
      transition: transition(fast);
    }
  }

  .al-global-primary-navigation {
    .al-pages-link {
      .icon {
        color: color(black, lighten, 1);
        font-size: 48px;
      }
    }
  }

  a.al-nav-link {
    align-items: center;
    display: flex;
    height: spacing(10);
    justify-content: center;
    margin: 2px 0;

    position: relative;

    text-decoration: none;
    width: spacing(10);

    &.al-nav-link-active.nuxt-link-exact-active,
    &.al-nav-link-active.al-pages-link {
      background-color: color(green, lighten, 3);
      border-radius: 50%;

      .icon {
        color: color(green, darken, 1);
      }
    }

    .al-tooltiptext {
      background-color: color(black);
      border-radius: 6px;

      color: color(white, darken, 2);
      font-size: font-size(s);
      height: spacing(8);
      left: calc(100% + #{spacing(1)});
      line-height: spacing(6);
      padding: spacing(1) spacing(4);

      position: absolute;
      text-transform: capitalize;
      top: spacing(2);
      transition: 0s visibility;
      visibility: hidden;

      width: max-content;
      z-index: 1;

      &.left-side {
        left: initial;
        right: calc(100% + #{spacing(1)});

        &::after {
          left: initial;
          right: -4px;
        }
      }

      &::after {
        background-color: #04100f;
        border: none;
        border-radius: 3px;
        border-style: solid;
        content: '';
        height: 11px;
        left: -4px;
        margin-top: -6px;
        position: absolute;
        right: 100%;
        top: 50%;
        transform: rotate(45deg);
        width: 11px;
      }
    }

    &:hover .al-tooltiptext {
      transition-delay: 0.5s;
      visibility: visible;
    }

    &:hover .al-contextual-menu > .pv-select.is-open ~ .al-tooltiptext {
      transition-delay: 0s;
      visibility: hidden;
    }

    &:hover {
      i.icon {
        color: color(green, lighten, 1);
      }
    }

    &:active {
      background-color: color(green, lighten, 3);
      border-radius: 50%;

      i.icon {
        color: color(green);
      }
    }
  }
}

@media (max-height: 840px) {
  .al-global-navigation {
    a.al-nav-link {
      height: spacing(8);
      width: spacing(8);
    }

    .al-global-primary-navigation .al-pages-link .icon {
      font-size: 36px;
    }
  }
}
</style>
